import { SetupDataStore } from '../stores/setupDataStore';
import { Provider } from '@servicetitan/financing-api-client';
import { useProviderEnabledForBusinessUnitResult } from './useIsProviderEnabledForBusinessUnit';
import { useTenantSettingsResult } from './useTenantSettings';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { SecondLookServiceDegradedStore } from '../stores/SecondLookServiceDegradedStore';
import * as providerConfigs from '../features/providers/configs';
import type { ProviderConfig } from '../features/providers/configs';

/**
 * Resolves a provider's base config by name. Used to look up the second-look provider's config so
 * field-level domains (e.g. valid residence types) can be derived from it rather than from
 * provider-specific flags. The module also exports helper functions, so non-object entries are
 * ignored.
 */
const getProviderConfigByName = (providerName: string): ProviderConfig | undefined => {
    const entry = (providerConfigs as Record<string, unknown>)[providerName.toLowerCase()];
    return entry !== null && typeof entry === 'object' ? (entry as ProviderConfig) : undefined;
};

/**
 * Configuration object describing all second-look integration state.
 *
 * The second-look provider used at decline-time is resolved dynamically from
 * `tenantSettings.allowedOnlineEstimatesSecondLookProviders[0]` for all flows. Technician flows
 * fall back to `'Turns'` only when the setting is absent or empty, preserving backward
 * compatibility for tenants that have not yet configured the field. Nothing in the shared
 * workflow/decision code path should hardcode a specific second-look provider name.
 */
export interface SecondLookIntegrationConfig {
    /**
     * Whether second look is fully enabled and ready to use.
     * All conditions must be true: provider supports waterfall + BU enabled + tenant enabled + not degraded.
     *
     * When enabled:
     * - Show second-look-specific fields (industry type for GreenSky/GoodLeap, income type for all)
     * - Show the second-look disclosure checkbox
     * - Automatically waterfall to the configured second-look provider after a primary decline
     */
    isEnabled: boolean;

    /**
     * Whether the first-look provider opts into the automatic second-look waterfall on decline.
     * True for ServiceFinance, GoodLeap, GreenSky; for WellsFargo it is additionally gated by the
     * `wellsFargoFinturfSecondLook` flag when Finturf is the second-look provider. False for
     * non-participating providers (e.g. Synchrony, BuyFin). True here doesn't mean fully enabled (see isEnabled).
     */
    providerFlagEnabled: boolean;

    /** Whether the second-look service is currently degraded/unavailable */
    isServiceDegraded: boolean;

    /** True while second-look enablement (provider-preference + tenant-settings) is still loading. */
    isLoading: boolean;

    /**
     * The resolved second-look provider name used for decline-time routing and copy.
     *
     * For all flows this is the first entry of
     * `tenantSettings.allowedOnlineEstimatesSecondLookProviders`. Technician flows fall back to
     * `'Turns'` when that list is absent or empty, preserving backward compatibility for tenants
     * that have not yet populated the setting.
     */
    secondLookProvider: string | null;

    /**
     * The config of the provider that will receive a second-look submission once the waterfall
     * fires, or `undefined` when no second-look waterfall applies for the current first-look
     * provider. Field-level domains (e.g. which residence types are valid) are derived from this
     * config rather than from any provider-specific flag, so adding a second-look provider with a
     * wider field domain is purely a config change. Intentionally excludes `isServiceDegraded` so
     * that transient outages do not cause form fields to disappear mid-session.
     */
    secondLookProviderConfig: ProviderConfig | undefined;
}

/**
 * Consolidated hook for all second-look integration logic.
 *
 * This hook provides a single source of truth for determining:
 * - Whether second look is enabled as a waterfall option
 * - Which provider the second look targets
 * - Whether to show second-look-specific UI elements
 *
 * Use this instead of checking provider-specific conditions or other second-look-related state directly.
 */
export const useSecondLookIntegration = (): SecondLookIntegrationConfig => {
    const { setupData } = SetupDataStore.useStore();
    const { isSecondLookServiceDegraded } = SecondLookServiceDegradedStore.useStore();
    const flags = useFlags();

    const { data: tenantSettings, isLoading: isTenantSettingsLoading } = useTenantSettingsResult();

    // Resolve the configured second-look provider so we can gate each BU-preference
    // query to only the relevant provider — avoiding a needless request for the other.
    const configuredSecondLookProviderRaw =
        tenantSettings?.allowedOnlineEstimatesSecondLookProviders?.[0] ?? null;

    // While tenant settings are still loading, `configuredSecondLookProviderRaw` is null.
    // In that state we run the Turns query as a safe default (no Finturf request fires yet).
    // Once settings resolve, exactly one query is enabled: Finturf tenants get the Finturf
    // check; everyone else (Turns, empty list, or still-loading) gets the Turns check.
    const isSettingsLoaded = tenantSettings !== undefined;
    const configuredIsFinturf =
        isSettingsLoaded && configuredSecondLookProviderRaw?.toLowerCase() === 'finturf';

    const turnsResult = useProviderEnabledForBusinessUnitResult(Provider.Turns, {
        enabled: !configuredIsFinturf,
    });
    const finturfResult = useProviderEnabledForBusinessUnitResult(Provider.Finturf, {
        enabled: configuredIsFinturf,
    });

    const { isEnabled: isEnabledForBusinessUnit } = configuredIsFinturf
        ? finturfResult
        : turnsResult;
    // Include both loading states so the readiness gate waits for whichever query is
    // active and prevents a false-settled flash when the selection flips Turns→Finturf.
    const isProviderPrefLoading = turnsResult.isLoading || finturfResult.isLoading;

    const isOnlineEstimatesFlow = setupData?.role !== 'technician';

    // Resolve the second-look provider from the tenant setting for all flows.
    // Technician flows fall back to 'Turns' when the list is absent or empty so that tenants
    // which haven't yet populated allowedOnlineEstimatesSecondLookProviders keep working.
    const secondLookProvider = isOnlineEstimatesFlow
        ? configuredSecondLookProviderRaw
        : (configuredSecondLookProviderRaw ?? 'Turns');

    // Second look is enabled for the tenant whenever a provider is configured, for all flows.
    // Both online-estimates and technician flows gate on provider presence only; there is no
    // separate enable toggle in the shared code path.
    const isEnabledForTenant = secondLookProvider != null;

    // Determine provider-specific configuration for the current (first-look) provider. This gates
    // whether the waterfall behavior applies and which provider config variant is used; it is not a
    // second-look routing decision.
    const provider = setupData?.provider?.toLowerCase();
    let providerFlagEnabled = false;

    if (provider === 'servicefinance') {
        providerFlagEnabled = true;
    } else if (provider === 'goodleap') {
        providerFlagEnabled = true;
    } else if (provider === 'greensky') {
        providerFlagEnabled = true;
    } else if (provider === 'wellsfargo') {
        // The WF→Finturf second-look path is gated by the wellsFargoFinturfSecondLook flag so it can
        // be rolled out/back independently; other second-look providers (e.g. Turns) stay unconditional.
        providerFlagEnabled = configuredIsFinturf ? !!flags.wellsFargoFinturfSecondLook : true;
    }

    // Overall enablement: all conditions must be true
    const isEnabled =
        isEnabledForBusinessUnit &&
        isEnabledForTenant &&
        providerFlagEnabled &&
        !isSecondLookServiceDegraded;

    // The provider that will receive a second-look submission once the waterfall fires. Gated on
    // providerFlagEnabled (not isServiceDegraded) so a transient outage doesn't change the form
    // mid-session. Field-level domains are derived from this config (see secondLookProviderConfig).
    const secondLookProviderConfig =
        providerFlagEnabled && secondLookProvider
            ? getProviderConfigByName(secondLookProvider)
            : undefined;

    return {
        isEnabled,
        providerFlagEnabled,
        isServiceDegraded: isSecondLookServiceDegraded,
        isLoading: isProviderPrefLoading || isTenantSettingsLoading,
        secondLookProvider,
        secondLookProviderConfig,
    };
};
